import { DateTime } from 'luxon';

// A missing number (no snapshot, or no baseline to divide by) is rendered as an
// em dash. Zero is a real measurement and must not collapse into it.
export const EM_DASH = '—';

/**
 * Whether a point of the rfmAnalysis response has numbers worth drawing.
 * `source: 'unavailable'` means no snapshot exists for that day.
 * @param {{source?: string}} [point]
 * @returns {boolean}
 */
export const isPointAvailable = (point) => Boolean(point) && point.source !== 'unavailable';

/**
 * Shape an rfmAnalysis response into one row per lifecycle stage, in the order
 * the API listed them. Counts from an unavailable point come back as `null` so
 * callers can tell "nothing recorded" from "recorded as zero".
 * @param {object} [analysis] - rfmAnalysis response body
 * @returns {Array<{stage: string, label: string, current: number|null, previous: number|null, percentChange: number|null, avgMonetaryScore: number|null}>}
 */
export const buildStageRows = (analysis) => {
  const current = isPointAvailable(analysis?.current) ? analysis.current : null;
  const previous = isPointAvailable(analysis?.previous) ? analysis.previous : null;
  const stageOrder = current?.stages || previous?.stages || [];
  const currentByStage = new Map((current?.stages || []).map((entry) => [entry.stage, entry]));
  const previousByStage = new Map((previous?.stages || []).map((entry) => [entry.stage, entry]));

  return stageOrder.map(({ stage, label }) => ({
    stage,
    label,
    current: currentByStage.get(stage)?.count ?? null,
    previous: previousByStage.get(stage)?.count ?? null,
    percentChange: analysis?.percentChange?.[stage] ?? null,
    avgMonetaryScore: currentByStage.get(stage)?.avgMonetaryScore ?? null,
  }));
};

/**
 * Format a percentChange value for display.
 * @param {number|null} [value]
 * @returns {string} e.g. "+12.5%", "-3.1%", or an em dash when there is no change to show
 */
export const formatPercentChange = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
};

/**
 * Format an avgMonetaryScore (1-5, `null` for an empty stage) for display.
 * @param {number|null} [value]
 * @returns {string}
 */
export const formatMonetaryScore = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return value.toFixed(1);
};

/**
 * Direction to colour a percentChange with, matching SummaryCard: moves under
 * 3% read as flat rather than as growth or decline.
 * @param {number|null} [value]
 * @returns {'positive'|'negative'|'neutral'|null} `null` when there is nothing to indicate
 */
export const percentChangeTone = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  if (Math.abs(value) < 3) {
    return 'neutral';
  }
  return value > 0 ? 'positive' : 'negative';
};

/**
 * Explain a missing point. Snapshots only started accruing when the stat that
 * records them was deployed, so an unavailable point is the expected state for
 * most dates and needs to read as such, not as a failure. Either end of the
 * comparison can be the missing one: `previous` (the start date) is the
 * realistic case right now, but `current` (the end date) is just as reachable
 * — the picker allows any end date back to `startDate`, including one from
 * before history began.
 * @param {object} [analysis] - rfmAnalysis response body
 * @returns {string|null} `null` when both points are available
 */
export const describeComparisonAvailability = (analysis) => {
  const previousAvailable = isPointAvailable(analysis?.previous);
  const currentAvailable = isPointAvailable(analysis?.current);
  if (previousAvailable && currentAvailable) {
    return null;
  }

  const missing = previousAvailable ? 'end' : 'start';
  const earliest = analysis?.earliestSnapshotDate;
  if (!earliest) {
    return 'History collection has only just started, so there is no earlier day to compare against yet.';
  }
  return `No snapshot was recorded for the ${missing} date. Historical comparison is available from ${DateTime.fromISO(
    earliest
  ).toFormat('d LLL yyyy')}.`;
};
